"use strict"

/**
 * Tax (VAT / GST / sales tax) helpers shared by the StackCP storefront.
 *
 * The basket service (customer-information-service, Stack\Basket\BasketItem)
 * checks every quoted item: it takes the ex-tax price as
 * round(quotedPriceFull - quotedVAT, 2) and accepts quotedVAT only if it equals
 * round(ex * rate / 100, 2), with PHP's round(). Quotes here are computed the
 * same way, with a single rounding of the tax, so what is shown is what is
 * accepted and charged.
 */
class TaxDisplay {
    /**
     * Rounds a money amount to 2dp exactly as PHP's round($v, 2) does.
     *
     * PHP rounds half away from zero after "pre-rounding" the value to 15
     * significant digits, so 1.15 * 10 / 100 (0.11499999999999999 as a float)
     * rounds to 0.12. `Math.round(v * 100) / 100` gives 0.11 there, which the
     * basket then rejects as the wrong VAT.
     *
     * @param {number|string} v
     * @returns {number}
     */
    static roundMoney(v) {
        const n = +v
        if(!isFinite(n) || n === 0) {
            return 0
        }
        const a = Math.abs(n)
        // 15 significant digits as an integer D, with a = D * 10^(e - 14)
        const [mantissa, exponent] = a.toExponential(14).split("e")
        const d = Number(mantissa.replace(".", ""))
        // Cents are D * 10^(e - 12)
        const shift = +exponent - 12
        let cents
        if(shift >= 0) {
            return n // Far beyond any price; PHP leaves these alone too
        } else if(-shift > 22) {
            cents = 0
        } else {
            // Exact integer / exact power of ten, so a true .5 stays a .5
            cents = Math.round(d / Math.pow(10, -shift))
        }
        const out = (n < 0 ? -cents : cents) / 100
        return out === 0 ? 0 : out // no -0
    }

    /**
     * Splits an ex-tax price into its tax and tax-inclusive total.
     *
     * @param {number|string} ex_tax eg. 1.15
     * @param {number|string|null} rate_percent eg. 20, 7.25
     * @returns {{ex: number, vat: number, full: number}}
     */
    static split(ex_tax, rate_percent) {
        const ex = TaxDisplay.roundMoney(ex_tax)
        const rate = +rate_percent || 0
        // Same order of operations as the server: ($ex * $rate) / 100
        const vat = TaxDisplay.roundMoney(ex * rate / 100)
        return {
            ex: ex,
            vat: vat,
            full: TaxDisplay.roundMoney(ex + vat),
        }
    }

    /**
     * The quote for a NEW basket item (basket add). Where US sales tax
     * applies the basket adds the tax itself, onto quotedPriceFull, so the
     * item has to be quoted ex-tax with no VAT. Everywhere else it is quoted
     * tax-inclusive.
     *
     * Modifying an existing item (BasketItem::modify) stores the figures as
     * given, so that always uses split() instead.
     *
     * @param {number|string} ex_tax
     * @param {number|string|null} rate_percent
     * @param {boolean} us_sales_tax
     * @returns {{quotedPriceFull: number, quotedVAT: number}}
     */
    static newItemQuote(ex_tax, rate_percent, us_sales_tax = false) {
        const s = TaxDisplay.split(ex_tax, rate_percent)
        if(us_sales_tax) {
            return {quotedPriceFull: s.ex, quotedVAT: 0}
        }
        return {quotedPriceFull: s.full, quotedVAT: s.vat}
    }

    /**
     * True if the basket will add US sales tax to new items itself.
     *
     * This mirrors Stack\Basket\VATDecision: a non-UK/EU reseller with US
     * sales tax on, selling to a StackCP user in the USA. It deliberately does
     * not use userConfig.reasonCode, because a reseller override can replace
     * the rate (reasonCode "custom_rate") while the basket still treats it as
     * sales tax.
     *
     * @param {?{usSalesTax?: boolean, location?: ?string}} user_config
     * @param {?{USSalesTaxEnabled?: boolean, isEUVATEntity?: boolean}} catalogue_config
     * @returns {boolean}
     */
    static isUSSalesTax(user_config, catalogue_config) {
        if(user_config && typeof user_config.usSalesTax == "boolean") {
            return user_config.usSalesTax
        }
        return !!(
            catalogue_config &&
            catalogue_config.USSalesTaxEnabled &&
            !catalogue_config.isEUVATEntity &&
            user_config &&
            user_config.location === "USA"
        )
    }

    /**
     * The name to show for the tax: the rate's own label (eg. "GST", "Sales
     * Tax"), else the brand's custom VAT label, else "VAT".
     *
     * @param {?{taxLabel?: ?string}} user_config
     * @param {?{customVatLabel?: ?string}} brand_info
     * @returns {string}
     */
    static label(user_config, brand_info = null) {
        if(user_config && user_config.taxLabel) {
            return user_config.taxLabel
        }
        if(brand_info && brand_info.customVatLabel) {
            return brand_info.customVatLabel
        }
        return TaxDisplay.translate("checkout_vat", "VAT")
    }

    /**
     * translation.get() returns the key itself when a key is missing, which
     * would put "checkout_reverse_charge_note" on the page. This returns the
     * English fallback instead.
     *
     * @param {string} key
     * @param {string} fallback
     * @returns {string}
     */
    static translate(key, fallback) {
        try {
            if(typeof translation != "undefined" && translation) {
                const v = translation.get(key)
                if(v && v !== key) {
                    return v
                }
            }
        } catch(e) {
            // Translations not loaded
        }
        return fallback
    }

    /**
     * ISO3166 alpha-3 to alpha-2 for the EU and UK VAT areas (the 27 EU
     * states, the UK and the Isle of Man).
     *
     * @type {{[alpha3: string]: string}}
     */
    static get vatAreaAlpha3() {
        return {
            AUT: "AT", BEL: "BE", BGR: "BG", CYP: "CY", CZE: "CZ",
            DEU: "DE", DNK: "DK", EST: "EE", ESP: "ES", FIN: "FI",
            FRA: "FR", GRC: "GR", HRV: "HR", HUN: "HU", IRL: "IE",
            ITA: "IT", LTU: "LT", LUX: "LU", LVA: "LV", MLT: "MT",
            NLD: "NL", POL: "PL", PRT: "PT", ROU: "RO", SWE: "SE",
            SVN: "SI", SVK: "SK",
            GBR: "GB", IMN: "IM",
        }
    }

    /**
     * @param {?string} cc ISO3166 alpha-2 or alpha-3
     * @returns {?string} alpha-2, if in the EU or UK VAT area
     */
    static vatAreaAlpha2(cc) {
        if(!cc) return null
        const c = ("" + cc).toUpperCase()
        const map = TaxDisplay.vatAreaAlpha3
        if(c.length == 3) {
            return map[c] || null
        }
        if(c == "EL") return "GR"
        if(c == "UK") return "GB"
        return Object.values(map).includes(c) ? c : null
    }

    /**
     * True if a VAT number can matter for a customer in this country: the EU
     * and UK VAT areas, including the Isle of Man.
     *
     * @param {?string} cc ISO3166 alpha-2 or alpha-3
     * @returns {boolean}
     */
    static isVatArea(cc) {
        return !!TaxDisplay.vatAreaAlpha2(cc)
    }

    /**
     * The country prefix a VAT number for this country carries, which is
     * also what the VAT number check (VIES / HMRC) takes: Greece is "EL" and
     * the Isle of Man uses UK ("GB") VAT numbers.
     *
     * @param {?string} cc ISO3166 alpha-2 or alpha-3
     * @returns {?string}
     */
    static vatPrefix(cc) {
        const a2 = TaxDisplay.vatAreaAlpha2(cc)
        switch(a2) {
            case null: return null
            case "GR": return "EL"
            case "IM": return "GB"
            default: return a2
        }
    }

    /**
     * Normalises what was typed into a VAT number field: no spaces, dots or
     * dashes, upper case, and without the country prefix if it was included.
     *
     * @param {?string} v eg. "de 123 456 789"
     * @param {?string} prefix eg. "DE"
     * @returns {string} eg. "123456789"
     */
    static vatNumberTail(v, prefix) {
        let s = ("" + (v || "")).replace(/[\s.\-]+/g, "").toUpperCase()
        if(prefix && s.indexOf(prefix) === 0) {
            s = s.substring(prefix.length)
        }
        return s
    }
}
